import React from 'react'

import { MedicalRecordForm } from 'models/MedicalRecord/MedicalRecordForm.type'

import { ListField } from '../components/ListField'
import { ReadOnlyField } from '../components/ReadOnlyField'
import {
  NEEDS_TO_CONTINUE_REASON_LABELS,
  PATIENT_CASE_COMPLEXITY_COLORS,
  PATIENT_CASE_COMPLEXITY_LABELS,
  PATIENT_CONSUMPTION_LABELS,
  PATIENT_GOAL_LABELS,
  SYMPTOMS_IMPACT_LABELS,
  formatBoolean,
} from '../utils/fieldLabels'
import { formatDiagnosticsList, formatMedicationsList } from '../utils/formatters'

import * as S from '../styles'

interface OutcomeViewProps {
  form: MedicalRecordForm
}

const isTrue = (value: unknown): boolean => value === true || value === 'true'
const isFalse = (value: unknown): boolean => value === false || value === 'false'

export function OutcomeView({ form }: OutcomeViewProps): JSX.Element {
  const complaintNames = form.complaints?.map((c) => c.complaint?.description).filter(Boolean) ?? []
  const symptomNames = form.symptoms?.map((s) => s.symptom?.description).filter(Boolean) ?? []
  const consumptionLabels =
    form.patient_consumptions
      ?.filter((c) => c !== 'OTHERS')
      .map((c) => PATIENT_CONSUMPTION_LABELS[c] ?? c) ?? []

  const needsToContinue = form.patient_needs_to_continue_treatment
  const needsToContinueAnswer = needsToContinue?.need_to_continue_treatment

  const needsToContinueBoolean: boolean | null = isTrue(needsToContinueAnswer)
    ? true
    : isFalse(needsToContinueAnswer)
      ? false
      : null

  const continuityValue = (): string | null => {
    if (needsToContinueBoolean === null) return null

    if (needsToContinueBoolean) {
      const n = needsToContinue?.number_of_schedules
      return n ? `Sim, ${n} consultas` : 'Sim'
    }

    const reasonLabel = needsToContinue?.reason && NEEDS_TO_CONTINUE_REASON_LABELS[needsToContinue.reason]
    const otherReason = needsToContinue?.other_reason

    if (reasonLabel === 'Outro' && otherReason) return `Não, Outro - ${otherReason}`
    if (reasonLabel) return `Não, ${reasonLabel}`
    return 'Não'
  }

  return (
    <S.Section>
      <ListField
        label='Queixa do paciente'
        items={complaintNames}
        otherText={form.other_complaint?.description}
      />
      <ListField
        label='Sintomas'
        items={symptomNames}
        otherText={form.other_symptom?.description}
      />
      <ReadOnlyField
        label='Qual o impacto destes sintomas na rotina de trabalho?'
        value={
          form.symptoms_impact_on_work_life &&
          (SYMPTOMS_IMPACT_LABELS[form.symptoms_impact_on_work_life] ?? form.symptoms_impact_on_work_life)
        }
      />
      <ReadOnlyField
        label='Paciente possui ideação suicida?'
        value={formatBoolean(form.has_suicidal_ideation)}
      />
      <ListField
        label='O paciente consome...'
        items={consumptionLabels}
        otherText={form.other_patient_consumption?.description}
      />
      <ListField
        label='Medicação de uso contínuo'
        items={formatMedicationsList(form)}
      />
      <ReadOnlyField
        label='Faz acompanhamento psiquiátrico?'
        value={formatBoolean(form.has_psychiatrist_treatment)}
      />
      <ReadOnlyField
        label='De acordo com a sua avaliação, qual a motivação do paciente para seguir com o tratamento?'
        value={form.patient_goal && (PATIENT_GOAL_LABELS[form.patient_goal] ?? form.patient_goal)}
      />
      <ReadOnlyField
        label='De acordo com a sua avaliação, qual é a complexidade atual do caso?'
        value={
          form.patient_case_complexity
            ? PATIENT_CASE_COMPLEXITY_LABELS[form.patient_case_complexity] ?? form.patient_case_complexity
            : null
        }
        valueColor={
          form.patient_case_complexity
            ? PATIENT_CASE_COMPLEXITY_COLORS[form.patient_case_complexity]
            : undefined
        }
      />
      <ReadOnlyField
        label='O paciente tem necessidade clínica de continuar o tratamento de psicoterapia?'
        value={continuityValue()}
      />
      <ReadOnlyField
        label='Breve descrição e indicação clínica'
        value={form.patient_case_description}
      />
      <ListField
        label='Hipótese(s) diagnóstica(s) – CID'
        items={formatDiagnosticsList(form)}
      />
      <ReadOnlyField
        label='Encaminhamento a profissionais da saúde'
        value={form.referral_to_healthcare_professional}
      />
    </S.Section>
  )
}
